import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { getAuthUser } from '../utils/auth';

const statusColors = {
  ACTIVE: 'bg-green-100 text-green-700',
  INACTIVE: 'bg-gray-100 text-gray-600',
};

export default function EmployeesTable() {
  const navigate = useNavigate();
  const authUser = getAuthUser();
  const isHr = authUser?.role === 'HR';
  const currentEmployeeId = authUser?.employeeId ?? null;
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  useEffect(() => {
    const loadEmployees = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get('/employees');
        setEmployees(response.data);
      } catch (err) {
        const message =
          err.response?.data?.message || 'No se pudieron cargar los empleados.';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadEmployees();
  }, []);

  const handleDelete = async (employeeId) => {
    const confirmDelete = window.confirm('¿Seguro que deseas eliminar este empleado?');
    if (!confirmDelete) {
      return;
    }
    setDeleteId(employeeId);
    setError(null);
    try {
      await axios.delete(`/employees/${employeeId}`);
      setEmployees((prev) => prev.filter((employee) => employee.id !== employeeId));
    } catch (err) {
      const message =
        err.response?.data?.message || 'No se pudo eliminar el empleado.';
      setError(message);
    } finally {
      setDeleteId(null);
    }
  };

  const departmentOptions = useMemo(() => {
    const options = new Map();
    let hasUnassigned = false;

    employees.forEach((employee) => {
      const departmentId = employee.departmentId;
      const departmentName = employee.departmentName;

      if (departmentId !== null && departmentId !== undefined) {
        const value = String(departmentId);
        const label = departmentName || `Departamento ${departmentId}`;
        if (!options.has(value)) {
          options.set(value, label);
        }
        return;
      }

      if (departmentName) {
        const value = `name:${departmentName}`;
        if (!options.has(value)) {
          options.set(value, departmentName);
        }
        return;
      }

      hasUnassigned = true;
    });

    if (hasUnassigned) {
      options.set('none', 'Sin departamento');
    }

    return Array.from(options, ([value, label]) => ({ value, label }));
  }, [employees]);

  const hrDepartmentId = useMemo(() => {
    if (!isHr || !currentEmployeeId) {
      return null;
    }
    const hrEmployee = employees.find((employee) => employee.id === currentEmployeeId);
    return hrEmployee?.departmentId ?? null;
  }, [employees, isHr, currentEmployeeId]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredEmployees = employees.filter((employee) => {
    const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
    const matchesName = normalizedSearch ? fullName.includes(normalizedSearch) : true;

    let matchesHrScope = true;
    if (isHr && hrDepartmentId) {
      const inDepartment = employee.departmentId === hrDepartmentId;
      const inactiveNoDepartment =
        (employee.departmentId === null || employee.departmentId === undefined) &&
        employee.status === 'INACTIVE';
      matchesHrScope = inDepartment || inactiveNoDepartment;
    }

    let matchesDepartment = true;
    if (!isHr && departmentFilter) {
      if (departmentFilter === 'none') {
        matchesDepartment = employee.departmentId === null || employee.departmentId === undefined;
      } else if (departmentFilter.startsWith('name:')) {
        matchesDepartment =
          (employee.departmentName || '').toLowerCase() ===
          departmentFilter.slice(5).toLowerCase();
      } else {
        matchesDepartment = String(employee.departmentId) === departmentFilter;
      }
    }

    return matchesName && matchesDepartment && matchesHrScope;
  });

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Empleados</h1>
          <p className="text-sm text-gray-500">Directorio base para control de presencia.</p>
        </div>
        <button
          onClick={() => navigate('/home/empleados/nuevo')}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          Alta de empleado
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="employee-search" className="text-sm font-semibold text-gray-700">
            Buscar por nombre
          </label>
          <input
            id="employee-search"
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Ej. Maria Perez"
            className="w-full max-w-sm rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="employee-department" className="text-sm font-semibold text-gray-700">
            Buscar por departamento
          </label>
          <select
            id="employee-department"
            value={departmentFilter}
            onChange={(event) => setDepartmentFilter(event.target.value)}
            disabled={isHr}
            className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="">Todos</option>
            {departmentOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="p-6 text-sm text-gray-500">Cargando empleados...</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-600">{error}</div>
        ) : filteredEmployees.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">
            No hay empleados registrados o no hay coincidencias.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Codigo
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Cargo
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredEmployees.map((employee) => {
                const statusClass = statusColors[employee.status] || statusColors.ACTIVE;
                const canManage =
                  !isHr ||
                  (hrDepartmentId && employee.departmentId === hrDepartmentId) ||
                  ((employee.departmentId === null || employee.departmentId === undefined) &&
                    employee.status === 'INACTIVE');
                return (
                  <tr key={employee.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {employee.firstName} {employee.lastName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{employee.employeeCode}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {employee.position || '--'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}>
                        {employee.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => navigate(`/home/empleados/editar/${employee.id}`)}
                          disabled={!canManage}
                          className="rounded bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-60"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(employee.id)}
                          disabled={deleteId === employee.id || !canManage}
                          className="rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                        >
                          {deleteId === employee.id ? 'Eliminando...' : 'Eliminar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
